<template>
  <section id="messages" class="messages-section">
    <div class="messages-container">
      <div class="section-badge">
        <span>{{ t.messages.badge }}</span>
      </div>

      <h2 class="section-title">{{ t.messages.title }}</h2>
      <span class="section-subtitle-gradient">{{ t.messages.tagline }}</span>

      <p class="section-subtitle">
        {{ t.messages.description }}
      </p>

      <!-- Interactive Text Effects Simulator -->
      <div class="effects-simulator">
        <div class="sim-header">
          <span class="sim-eyebrow">{{ t.messages.simEyebrow }}</span>
          <h3 class="sim-title">{{ t.messages.simTitle }}</h3>
          <p class="sim-desc">{{ t.messages.simDesc }}</p>
        </div>

        <div class="chat-mockup-area">
          <div class="imessage-bubble" :class="activeEffect">
            <span class="bubble-text">
              {{ activeEffectText }}
            </span>
            <div class="bubble-tapback">
              <span>{{ activeTapback }}</span>
            </div>
          </div>
        </div>

        <div class="effects-selector">
          <button
            v-for="(effect, idx) in localizedEffectsList"
            :key="idx"
            @click="triggerEffect(effect)"
            class="effect-btn"
            :class="{ active: activeEffect === effect.id }"
          >
            {{ effect.name }}
          </button>
        </div>

        <div class="tapback-selector">
          <span class="tapback-label">{{ t.messages.tapbackLabel }}</span>
          <div class="tapback-emojis">
            <button
              v-for="(emoji, eIdx) in emojiList"
              :key="eIdx"
              @click="activeTapback = emoji"
              class="emoji-btn"
              :class="{ selected: activeTapback === emoji }"
            >
              {{ emoji }}
            </button>
          </div>
        </div>
      </div>

      <!-- Satellite & Scheduling Feature Grid -->
      <div class="messages-sub-grid">
        <div class="msg-card">
          <div class="msg-icon-box">
            <span>📡</span>
          </div>
          <h4 class="msg-card-title">{{ isEcuador ? 'Mensajes vía Satélite' : 'Messages via Satellite' }}</h4>
          <p class="msg-card-desc">
            {{ isEcuador ? 'Cuando estás sin Wi-Fi o cobertura celular en la sierra o el oriente, conéctate directamente al satélite más cercano para enviar y recibir iMessages y SMS.' : 'When you’re off the grid without cellular or Wi-Fi coverage, connect directly to the nearest satellite to send and receive iMessage and SMS.' }}
          </p>
        </div>

        <div class="msg-card">
          <div class="msg-icon-box">
            <span>⏱</span>
          </div>
          <h4 class="msg-card-title">{{ isEcuador ? 'Enviar más Tarde' : 'Send Later' }}</h4>
          <p class="msg-card-desc">
            {{ isEcuador ? 'Programa un mensaje para que se envíe a una hora específica, perfecto para saludos nocturnos o recordatorios importantes.' : 'Schedule a message to send at a designated time — ideal for when it’s late at night or you want to remind someone at just the right moment.' }}
          </p>
        </div>

        <div class="msg-card">
          <div class="msg-icon-box">
            <span>🎨</span>
          </div>
          <h4 class="msg-card-title">{{ isEcuador ? 'Formato Enriquecido y Tapbacks' : 'Richer Formatting & Tapbacks' }}</h4>
          <p class="msg-card-desc">
            {{ isEcuador ? 'Agrega negrita, cursiva, subrayado o tachado a cualquier texto. Reacciona con cualquier emoji de tu teclado.' : 'Add bold, italic, underline, or strikethrough formatting to any letter or word. React with any emoji or Live Sticker via Tapback.' }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useI18n } from '@/i18n';

export default {
  name: 'MessagesSection',
  setup() {
    const { t, isEcuador } = useI18n();
    return { t, isEcuador };
  },
  data() {
    return {
      activeEffect: 'effect-big',
      activeTapback: '❤️',
      emojiList: ['❤️', '👍', '👎', '😂', '‼️', '❓', '🎉', '🔥']
    };
  },
  computed: {
    localizedEffectsList() {
      return [
        { id: 'effect-big', name: this.isEcuador ? 'Grande' : 'Big', text: this.t.messages.sampleBig },
        { id: 'effect-bounce', name: this.isEcuador ? 'Rebotar' : 'Bounce', text: this.t.messages.sampleBounce },
        { id: 'effect-glow', name: this.isEcuador ? 'Brillo' : 'Glow', text: this.t.messages.sampleGlow },
        { id: 'effect-shake', name: this.isEcuador ? 'Sacudir' : 'Shake', text: this.t.messages.sampleShake }
      ];
    },
    activeEffectText() {
      const match = this.localizedEffectsList.find(e => e.id === this.activeEffect);
      return match ? match.text : this.t.messages.sampleText;
    }
  },
  methods: {
    triggerEffect(effect) {
      this.activeEffect = '';
      this.$nextTick(() => {
        this.activeEffect = effect.id;
      });
    }
  }
}
</script>

<style scoped>
.messages-section {
  padding: 100px 24px;
  background: #f5f5f7;
  color: #1d1d1f;
  text-align: center;
}

.messages-container {
  max-width: 1120px;
  margin: 0 auto;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.06);
  color: #1d1d1f;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 980px;
  margin-bottom: 20px;
}

.section-title {
  font-size: clamp(34px, 5.5vw, 56px);
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1.07143;
  color: #1d1d1f;
  margin-bottom: 4px;
}

.section-subtitle-gradient {
  font-size: clamp(30px, 5vw, 52px);
  line-height: 1.1;
  font-weight: 600;
  letter-spacing: 0.004em;
  background: linear-gradient(to right,
      #ff3b30,
      #ff9500,
      #b59722,
      #4c63d9,
      #187099,
      #007aff,
      #36349e,
      #3211b4);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  display: inline-block;
  margin-bottom: 20px;
}

.section-subtitle {
  max-width: 760px;
  margin: 0 auto 50px;
  font-size: 17px;
  line-height: 1.45;
  color: #86868b;
  font-weight: 400;
}

/* Effects Simulator */
.effects-simulator {
  background: #ffffff;
  border-radius: 28px;
  padding: 40px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.06);
  margin-bottom: 50px;
}

.sim-header {
  max-width: 600px;
  margin: 0 auto 30px;
}

.sim-eyebrow {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  color: #0071e3;
  display: block;
  margin-bottom: 8px;
}

.sim-title {
  font-size: 28px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 8px;
}

.sim-desc {
  font-size: 15px;
  color: #86868b;
}

.chat-mockup-area {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
}

.imessage-bubble {
  position: relative;
  background: #007aff;
  color: #ffffff;
  padding: 16px 28px;
  border-radius: 26px;
  font-size: 22px;
  font-weight: 500;
  box-shadow: 0 8px 24px rgba(0, 122, 255, 0.3);
  transition: all 0.3s ease;
}

.bubble-tapback {
  position: absolute;
  top: -16px;
  left: -10px;
  background: #ffffff;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 18px;
}

/* Animations */
.effect-big {
  animation: bigScale 0.8s ease forwards;
}

@keyframes bigScale {
  0% { transform: scale(0.8); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1.05); }
}

.effect-bounce {
  animation: bounceAnim 0.8s ease infinite alternate;
}

@keyframes bounceAnim {
  0% { transform: translateY(0); }
  100% { transform: translateY(-16px); }
}

.effect-glow {
  animation: glowAnim 1.5s ease infinite alternate;
}

@keyframes glowAnim {
  0% {
    box-shadow: 0 0 10px rgba(0, 122, 255, 0.4);
    background: #007aff;
  }
  100% {
    box-shadow: 0 0 40px rgba(255, 149, 0, 0.8), 0 0 20px rgba(255, 59, 48, 0.6);
    background: linear-gradient(135deg, #ff3b30, #ff9500);
  }
}

.effect-shake {
  animation: shakeAnim 0.4s ease-in-out infinite alternate;
}

@keyframes shakeAnim {
  0% { transform: rotate(-3deg) scale(1.02); }
  100% { transform: rotate(3deg) scale(1.02); }
}

.effects-selector {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.effect-btn {
  padding: 8px 20px;
  border-radius: 980px;
  border: 1px solid #d2d2d7;
  background: #ffffff;
  color: #1d1d1f;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.effect-btn:hover {
  border-color: #0071e3;
  color: #0071e3;
}

.effect-btn.active {
  background: #0071e3;
  color: #ffffff;
  border-color: #0071e3;
}

.tapback-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
}

.tapback-label {
  font-size: 14px;
  color: #86868b;
  font-weight: 500;
}

.tapback-emojis {
  display: flex;
  gap: 8px;
}

.emoji-btn {
  background: #f5f5f7;
  border: 1px solid transparent;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s;
}

.emoji-btn:hover {
  transform: scale(1.2);
}

.emoji-btn.selected {
  border-color: #0071e3;
  background: rgba(0, 113, 227, 0.1);
  transform: scale(1.15);
}

/* Sub grid */
.messages-sub-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  text-align: left;
}

.msg-card {
  background: #ffffff;
  padding: 32px 28px;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
}

.msg-icon-box {
  font-size: 28px;
  margin-bottom: 16px;
}

.msg-card-title {
  font-size: 20px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 10px;
}

.msg-card-desc {
  font-size: 15px;
  line-height: 1.45;
  color: #6e6e73;
}
</style>
